// ============================================================================
// LES MONTANTS D'UNE COMMANDE FOURNISSEUR — brut, remise, port, net, escompte. Une seule règle pour
// l'éditeur, le bon de commande et le serveur.
//
// UTILITÉ : la commande annonce ce que le magasin paiera ; l'écran qui la compose, le document envoyé au
// fournisseur et le serveur qui l'enregistre doivent dire le MÊME net. Un écran qui calculerait le sien
// finirait par afficher un centime de plus que le PDF, et c'est le PDF que le fournisseur facture.
//
// ⚠️ ARRONDI À CHAQUE ÉTAPE VISIBLE, pas un seul arrondi final : le document affiche Brut − Remise + Port
//    = Net, et ces quatre montants doivent s'additionner à la calculette. Le brut est arrondi au centime
//    une fois, la remise est calculée sur ce brut affiché — la ligne « Net » est alors exacte par
//    construction (même décision que les remises empilées en caisse).
// ⚠️ LE PORT EST UN MONTANT DE LA COMMANDE, JAMAIS UN COÛT D'ARTICLE (décision utilisateur) : il ne se
//    répartit sur aucune ligne et n'entre pas dans le prix d'achat qui fera le PMP.
// ⚠️ LA REMISE BAISSE LE PRIX DE LA COMMANDE, ET CE PRIX PASSE AU BL (décision utilisateur Q1) : le prix
//    d'achat qu'un BL reprend d'une commande est le prix unitaire NET de la remise (`netUnitPriceFine`) ;
//    il ne touche le PMP qu'à l'entrée en stock, comme tout prix de BL.
// ⚠️ L'ESCOMPTE NE TOUCHE NI LE NET NI LE PRIX D'ACHAT : il dit ce qui sera payé si le règlement tombe
//    dans le délai (« net à payer »), calculé sur le net, port compris.
// ============================================================================
import { applyBp, rescale } from '@orgazone/core-money';

/** Une ligne de commande : sa quantité et son prix unitaire HT à l'échelle FINE (4 décimales) — `null` = sans prix. */
export interface PurchaseOrderLine {
  qty: number;
  unitPriceFine: number | null;
}

/** Les conditions de la commande, toutes facultatives (absent = 0). */
export interface PurchaseOrderTerms {
  discountBp?: number | null;
  shippingCents?: number | null;
  earlyPaymentBp?: number | null;
}

export interface PurchaseOrderTotals {
  /** Pièces commandées — la somme des quantités. */
  pieces: number;
  /** Brut HT, au centime : Σ quantité × prix, arrondi une fois. */
  grossCents: number;
  /** Remise fournisseur, au centime, calculée sur le brut AFFICHÉ. */
  discountCents: number;
  /** Port HT — compté seulement si la commande porte au moins une pièce. */
  shippingCents: number;
  /** Net HT = brut − remise + port. */
  netCents: number;
  /** Escompte pour paiement anticipé, sur le net. 0 sans escompte. */
  earlyPaymentCents: number;
  /** Net à payer si l'escompte est pris = net − escompte. */
  netToPayCents: number;
  /**
   * Lignes commandées SANS prix — le brut ne les compte pas.
   * ⚠️ Le dire à l'écran : un total qui ignore des lignes en silence passerait pour le total.
   */
  unpricedLines: number;
}

const assertBp = (bp: number, what: string) => {
  if (!Number.isInteger(bp) || bp < 0 || bp > 10_000) throw new Error(`core-inventory: ${what} en points de base entre 0 et 10 000 attendu (${bp})`);
};

/**
 * Les montants d'une commande — le brut, la remise, le port, le net et l'escompte.
 * Lève sur une quantité ou un prix non entier : ces valeurs viennent toujours d'une saisie déjà lue.
 */
export function purchaseOrderTotals(lines: readonly PurchaseOrderLine[], terms: PurchaseOrderTerms = {}): PurchaseOrderTotals {
  const discountBp = terms.discountBp ?? 0;
  const earlyPaymentBp = terms.earlyPaymentBp ?? 0;
  assertBp(discountBp, 'remise');
  assertBp(earlyPaymentBp, 'escompte');
  const shipping = terms.shippingCents ?? 0;
  if (!Number.isSafeInteger(shipping) || shipping < 0) throw new Error(`core-inventory: port en centimes entier ≥ 0 attendu (${shipping})`);

  let pieces = 0;
  let grossFine = 0;
  let unpriced = 0;
  for (const l of lines) {
    if (!Number.isSafeInteger(l.qty) || l.qty < 0) throw new Error(`core-inventory: quantité entière ≥ 0 attendue (${l.qty})`);
    if (l.qty === 0) continue;
    pieces += l.qty;
    if (l.unitPriceFine == null) { unpriced += 1; continue; }
    if (!Number.isSafeInteger(l.unitPriceFine) || l.unitPriceFine < 0) throw new Error(`core-inventory: prix fin entier ≥ 0 attendu (${l.unitPriceFine})`);
    grossFine += l.qty * l.unitPriceFine;
  }
  if (!Number.isSafeInteger(grossFine)) throw new Error('core-inventory: brut hors des entiers sûrs');

  const grossCents = rescale(grossFine, 4, 2);
  const discountCents = applyBp(grossCents, discountBp);
  // ⚠️ Pas de port sur une commande vide : l'éditeur pré-remplit le port dès le premier article, et une
  //    grille encore à zéro n'est pas une commande qui coûte un transport.
  const shippingCents = pieces > 0 ? shipping : 0;
  const netCents = grossCents - discountCents + shippingCents;
  const earlyPaymentCents = applyBp(netCents, earlyPaymentBp);
  return {
    pieces, grossCents, discountCents, shippingCents, netCents,
    earlyPaymentCents, netToPayCents: netCents - earlyPaymentCents, unpricedLines: unpriced,
  };
}

/**
 * Le prix unitaire NET de la remise, à l'échelle fine — celui qu'un BL reprend de la commande.
 * ⚠️ À l'échelle fine (sous-centime) : arrondi ici au centime, une remise de 2,5 % sur 9,99 € perdrait
 *    une fraction de centime par pièce, et le PMP en hériterait pour toujours.
 */
export function netUnitPriceFine(unitPriceFine: number, discountBp: number | null | undefined): number {
  const bp = discountBp ?? 0;
  assertBp(bp, 'remise');
  if (!Number.isSafeInteger(unitPriceFine) || unitPriceFine < 0) throw new Error(`core-inventory: prix fin entier ≥ 0 attendu (${unitPriceFine})`);
  return applyBp(unitPriceFine, 10_000 - bp);
}
